/**
 * Types shared between the main thread and the mesher workers.
 *
 * A mesh job carries a padded copy of one chunk column plus a one-block border
 * from its neighbours, clipped to the vertical span that actually contains
 * blocks. That is enough for face culling, ambient occlusion and smooth
 * lighting at the chunk seams, and it keeps the world itself single-copy on the
 * main thread instead of mirrored into every worker.
 */

/** Padded footprint: 16 blocks plus one border block on each side. */
export const PAD = 1;
export const JOB_W = 16 + PAD * 2;

export interface MeshJob {
  id: number;
  cx: number;
  cz: number;
  /** World Y of the first layer in `blocks`. */
  minY: number;
  /** Number of Y layers included. */
  height: number;
  /** JOB_W * height * JOB_W block states, indexed [y][z][x]. */
  blocks: Uint16Array;
  skyLight: Uint8Array;
  blockLight: Uint8Array;
  /** JOB_W * JOB_W biome ids for tinting. */
  biomes: Uint8Array;
  /** Incremented each time the chunk changes, so stale results are dropped. */
  revision: number;
}

export interface MeshLayer {
  /** Interleaved vertex data; see VERTEX_STRIDE. */
  vertices: ArrayBuffer;
  indices: ArrayBuffer;
  indexCount: number;
  /** True when indices are Uint32Array rather than Uint16Array. */
  wideIndices: boolean;
}

export interface MeshResult {
  id: number;
  cx: number;
  cz: number;
  revision: number;
  minY: number;
  opaque: MeshLayer;
  cutout: MeshLayer;
  translucent: MeshLayer;
  /** World-space vertical extent of the produced geometry, for culling. */
  boundsMinY: number;
  boundsMaxY: number;
  /** Milliseconds spent meshing, for the debug overlay. */
  ms: number;
}

/**
 * Vertex layout, 20 bytes:
 *   0  u16 x, y, z      position in 1/32 block units, relative to the job origin
 *   6  u16 light        skyLight | blockLight << 4
 *   8  u16 u, v         texture coordinates in 1/64 tile units
 *   12 u16 texLayer     array-texture layer
 *   14 u16 normal       face index 0..5
 *   16 u8  r, g, b, a   baked tint * ambient occlusion * face shading
 */
export const VERTEX_STRIDE = 20;
export const POS_SCALE = 32;
export const UV_SCALE = 64;

export function layerIsEmpty(layer: MeshLayer): boolean {
  return layer.indexCount === 0;
}

export const EMPTY_LAYER: MeshLayer = {
  vertices: new ArrayBuffer(0),
  indices: new ArrayBuffer(0),
  indexCount: 0,
  wideIndices: false,
};
